// AddonLockedBanner — shown ONLY while a platform admin is "Viewing as" a
// tenant AND has opened a module whose add-on is locked for that tenant. The
// beta unlock still lets an admin open the module for support/inspection, but
// this slim banner keeps the truth clear so support screenshots don't imply
// the tenant actually has the feature on their dashboard/sidebar.

import { useLocation } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { navItems } from './navItems';
import { isModuleUnlocked } from './helpers';

// Longest-prefix match of the current path to an add-on-gated nav item.
// Root ('/') is skipped so it never matches everything.
function matchAddonNavItem(pathname) {
  let best = null;
  for (const item of navItems) {
    if (!item.addon && !item.anyAddon) continue;
    if (item.to === '/') continue;
    if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
      if (!best || item.to.length > best.to.length) best = item;
    }
  }
  return best;
}

export function AddonLockedBanner() {
  const { user } = useAuth();
  const { pathname } = useLocation();

  if (!user?._impersonating_tenant) return null;

  const item = matchAddonNavItem(pathname);
  if (!item) return null;

  const tenantFlags = user?.tenant_addon_flags || {};
  const unlocked = item.anyAddon
    ? item.anyAddon.some((a) => isModuleUnlocked(a, tenantFlags))
    : isModuleUnlocked(item.addon, tenantFlags);
  if (unlocked) return null;

  return (
    <div
      data-testid="addon-locked-banner"
      className="flex items-center justify-center gap-2 px-4 py-1.5 print:hidden"
      style={{ background: '#EEF2F7', borderBottom: '1px solid #D5DCE6' }}
    >
      <Lock className="w-3.5 h-3.5 text-[#3B4A63] flex-shrink-0" />
      <span className="text-[11px] font-medium text-[#3B4A63] text-center">
        <strong>{item.label}</strong> is locked for this tenant — you&apos;re seeing it via admin access.
        It doesn&apos;t appear on their dashboard or sidebar.
      </span>
    </div>
  );
}
